import React, { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import {
  NumberParam,
  StringParam,
  createEnumParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { usePageTitle } from "@/atoms/page-title-atom";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlide } from "@components/title-slide-view";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { BreakdownParam } from "@features/sales-v3/context";
import { BreakdownSelector } from "@features/sales-v3/shared";
import {
  GET_LABEL_ASSIGNMENT,
  GET_LABEL_ASSIGNMENT_COUNT,
} from "@gql/label-assignment";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetLabelAssignment,
  type GetLabelAssignmentCount,
  type GetLabelAssignmentCountVariables,
  type GetLabelAssignmentVariables,
} from "@north-beam/nb-client-side";
import { CustomJsonCompressedParam } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { Paginator } from "@shared/paginator";
import { Select } from "@shared/selects";

import { type Sorting } from "../edit-breakdown/advanced-editor/types";

import { BulkUpdatingLabelsModal } from "./bulk-updating-labels-modal";
import { SearchInput } from "./search-input";
import { Table } from "./table";
import { Tabs, type TabId } from "./tabs";

export interface Selection {
  mode: "inclusive" | "exclusive";
  ids: string[];
}

const PER_PAGE = 20;

const SortingParam = CustomJsonCompressedParam<Sorting>();
const GranularityParam = createEnumParam<TabId>(["campaign", "adset", "ad"]);

export const LabelsAssignment = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [, setPageTitle] = usePageTitle();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const [filters, setFilters] = useQueryParams({
    status: withDefault(StringParam, "all"),
    breakdowns: withDefault(BreakdownParam, []),
    manualOverride: withDefault(StringParam, "all"),
    search: withDefault(StringParam, ""),
    granularity: withDefault(GranularityParam, "campaign"),
    page: withDefault(NumberParam, 1),
    sort: withDefault(SortingParam, { column: "name", order: "asc" }),
  });

  useEffect(() => {
    setPageTitle("Label assignment");
  }, [setPageTitle]);

  const queryVariables = {
    status: filters.status,
    breakdowns: filters.breakdowns.flatMap(({ key, values }) =>
      values.map((value) => ({ breakdown: key, value })),
    ),
    manualOverride: filters.manualOverride,
    search: filters.search,
    granularity: filters.granularity,
    sort: filters.sort,
    page: filters.page,
    perPage: PER_PAGE,
  };

  const { data: rowData, loading } = useNorthbeamQuery<
    GetLabelAssignment,
    GetLabelAssignmentVariables
  >(GET_LABEL_ASSIGNMENT, {
    useCpuIntensive: true,
    variables: queryVariables,
  });

  const { data: countData } = useNorthbeamQuery<
    GetLabelAssignmentCount,
    GetLabelAssignmentCountVariables
  >(GET_LABEL_ASSIGNMENT_COUNT, {
    useCpuIntensive: true,
    variables: {
      status: filters.status,
      breakdowns: filters.breakdowns.flatMap(({ key, values }) =>
        values.map((value) => ({ breakdown: key, value })),
      ),
      manualOverride: filters.manualOverride,
      search: filters.search,
    },
  });
  const [isUpdatingLabelsModal, setIsUpdatingLabelsModal] = useState(false);
  const [selection, setSelection] = useState<Selection>({
    mode: "inclusive",
    ids: [],
  });

  const totalRows = useMemo(() => {
    if (!countData) {
      return 0;
    }

    if (filters.granularity === "campaign") {
      return countData.me.campaignLabelAssignmentCount;
    }

    if (filters.granularity === "adset") {
      return countData.me.adsetLabelAssignmentCount;
    }

    if (filters.granularity === "ad") {
      return countData.me.adLabelAssignmentCount;
    }

    return 0;
  }, [filters.granularity, countData]);

  const selectionCount = useMemo(() => {
    if (selection.mode === "inclusive") {
      return selection.ids.length;
    } else {
      return totalRows - selection.ids.length;
    }
  }, [totalRows, selection]);

  return (
    <div
      className={clsx(
        "bg-white border border-gray rounded-b-lg mt-[-1px] p-4",
        "h-[calc(100vh-56px-40px-48px)] grid grid-rows-[min-content_auto]",
      )}
    >
      <div>
        <div className="flex items-center gap-4 mb-6">
          <Select
            className="w-[160px]"
            label="STATUS"
            value={filters.status}
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
            onChange={(status) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo: "Change label assignment filter status",
                data: {
                  newStatus: status,
                  pageContext: { ...filters },
                },
              });

              setSelection({ mode: "inclusive", ids: [] });
              setFilters({ status });
            }}
          />
          <div>
            <div
              className={clsx(
                "text-xs text-interstellar+2 mb-1 leading-4 tracking-[0.025em] cursor-default",
              )}
            >
              BREAKDOWN FILTER
            </div>
            <BreakdownSelector
              mode="breakdowns-and-values"
              breakdownConfigs={breakdownConfigs}
              selectedBreakdowns={filters.breakdowns}
              showResetButton
              closeOnReset
              buttonClassName={clsx(
                "button sm secondary-button rounded-md h-8 w-[160px] text-sm px-3",
                "hover:bg-white",
              )}
              buttonActiveClassName={clsx(
                "button sm secondary-button rounded-md h-8 w-[160px] text-sm px-3",
                "border-cosmic ring-1 ring-cosmic hover:bg-white",
              )}
              buttonContentComponent={({ isOpen }) => (
                <div className="flex items-center justify-between">
                  <span>
                    {filters.breakdowns.length === 0
                      ? "All"
                      : `${filters.breakdowns.length} Selected`}
                  </span>
                  <span>
                    <motion.i
                      initial={false}
                      animate={{ rotate: isOpen ? 0 : 180 }}
                      transition={{ duration: 0.2 }}
                      className={clsx("fa-regular fa-sharp fa-chevron-up", {
                        "text-interstellar": !isOpen,
                        "text-cosmic+1": isOpen,
                      })}
                    />
                  </span>
                </div>
              )}
              menuPlacement="bottom-start"
              onSelectionChange={(breakdowns) => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onChange",
                  additionalActionInfo:
                    "Change label assignment filter breakdowns",
                  data: {
                    newBreakdowns: breakdowns,
                    pageContext: { ...filters },
                  },
                });

                setSelection({ mode: "inclusive", ids: [] });
                setFilters({ breakdowns });
              }}
              onDelete={() => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onChange",
                  additionalActionInfo:
                    "Clear label assignment filter breakdowns",
                  data: {
                    newBreakdowns: [],
                    pageContext: { ...filters },
                  },
                });

                setSelection({ mode: "inclusive", ids: [] });
                setFilters({ breakdowns: [] });
              }}
            />
          </div>
          <Select
            className="w-[190px]"
            label="MANUALLY OVERRIDEN"
            value={filters.manualOverride}
            options={[
              { value: "all", label: "All" },
              { value: "yes", label: "Manually Assigned" },
              { value: "no", label: "Not Manually Assigned" },
            ]}
            onChange={(manualOverride) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo:
                  "Change label assignment filter manually overriden",
                data: {
                  newManualOverride: manualOverride,
                  pageContext: { ...filters },
                },
              });

              setSelection({ mode: "inclusive", ids: [] });
              setFilters({ manualOverride });
            }}
          />
        </div>
        <div className="mb-4 flex items-center gap-6 justify-between">
          <SearchInput
            value={filters.search}
            onChange={(search) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onChange",
                additionalActionInfo: "Change label assignment filter search",
                data: {
                  newSearch: search,
                  pageContext: { ...filters },
                },
              });

              setSelection({ mode: "inclusive", ids: [] });
              setFilters({ search });
            }}
          />
          <div className="flex gap-2">
            <div className="flex flex-row gap-2">
              <PrimaryButton
                onClick={() => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onClick",
                    additionalActionInfo: "Open update label assignments modal",
                  });

                  setIsUpdatingLabelsModal(true);
                }}
                disabled={selectionCount === 0}
                className="uppercase font-semibold flex items-center gap-2"
              >
                <i className="fa-regular fa-pen"></i>
                <span>
                  Update Selected
                  {selectionCount > 0 ? ` (${selectionCount})` : ""}
                </span>
              </PrimaryButton>
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-end justify-between gap-12">
            <Tabs
              campaignsCount={countData?.me.campaignLabelAssignmentCount || 0}
              adsetsCount={countData?.me.adsetLabelAssignmentCount || 0}
              adsCount={countData?.me.adLabelAssignmentCount || 0}
              activeTab={filters.granularity as TabId}
              onActivate={(tab) => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Change label assignment tab",
                  data: {
                    newGranularity: tab,
                    pageContext: { ...filters },
                  },
                });

                setSelection({ mode: "inclusive", ids: [] });
                setFilters({
                  page: 1,
                  granularity: tab,
                });
              }}
            />
          </div>
          <div className="border-b border-gray mt-[-1px]"></div>
        </div>
      </div>
      <div
        className={clsx("grid overflow-hidden", {
          "grid-rows-[auto_min-content]": loading,
          "grid-rows-[minmax(auto,min-content)_min-content]": !loading,
        })}
      >
        <div
          className={clsx(
            "border-b border-x border-gray rounded-b-lg overflow-auto",
          )}
        >
          {!loading && rowData ? (
            <>
              {rowData.me.labelAssignment.length > 0 ? (
                <Table
                  rows={rowData.me.labelAssignment}
                  totalRows={totalRows}
                  selection={selection}
                  onChangeSelection={(value) => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onChange",
                      additionalActionInfo: "Change label assignment selection",
                      data: {
                        newSelection: selection,
                        pageContext: { selection },
                      },
                    });

                    setSelection(value);
                  }}
                  sorting={filters.sort}
                  onChangeSorting={(sort) => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onChange",
                      additionalActionInfo: "Change label assignment sorting",
                      data: {
                        newSort: sort,
                        pageContext: { ...filters },
                      },
                    });

                    setFilters({ sort });
                  }}
                />
              ) : (
                <div className="py-24 px-6 text-gray">NO RESULTS FOUND</div>
              )}
            </>
          ) : (
            <LoadingSlide />
          )}
        </div>
        {!loading && rowData && rowData.me.labelAssignment.length > 0 && (
          <div className="flex justify-end mt-4">
            <Paginator
              page={filters.page}
              perPage={PER_PAGE}
              total={totalRows}
              onChange={(page) => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onChange",
                  additionalActionInfo: "Change label assignment page",
                  data: {
                    newPage: page,
                    pageContext: { ...filters },
                  },
                });

                setFilters({ page });
              }}
            />
          </div>
        )}
      </div>
      <BulkUpdatingLabelsModal
        isOpen={isUpdatingLabelsModal}
        selection={selection}
        totalRows={totalRows}
        activeTab={filters.granularity as TabId}
        queryVariables={queryVariables}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onChange",
            additionalActionInfo: "Close update label assignments modal",
          });

          setIsUpdatingLabelsModal(false);
        }}
        onChange={() => setSelection({ mode: "inclusive", ids: [] })}
      />
    </div>
  );
};
